import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { Upload } from "@toddle-edu/ds-web";
import { ProfileOutlined } from "@toddle-edu/ds-icons";
import { CreateDropDown } from "Attachment";

class ProfilePhotoPickerV2 extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      uploadObj: {},
      croppedFile: undefined,
      file: "",
      name: "",
    };
    this.dropzoneRef = React.createRef();
  }

  waitFor = conditionFunction => {
    const helper = resolve => {
      if (conditionFunction()) resolve();
      else setTimeout(() => helper(resolve), 500);
    };

    return new Promise(helper);
  };
  croppedFile = async file => {
    // Use setState callback promise to ensure state is updated before proceeding
    await new Promise(resolve => {
      this.setState(
        {
          file: file.preview,
          croppedFile: undefined,
          name: file.name,
        },
        // Resolve promise when state update is complete, making this setState synchronous,
        // the file from state is expected in the condition: `!this.state.file`
        resolve
      );
    });

    this.props.showCroppingModal &&
      (await this.waitFor(() => this.state.croppedFile || !this.state.file));
    return this.props.showCroppingModal ? this.state.croppedFile : file;
  };
  onCancel = () => {
    this.setState({ file: "" });
  };
  dataURLtoBlob = dataUrl => {
    var arr = dataUrl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  };
  onAddContent = async dataImage => {
    const blob = this.dataURLtoBlob(dataImage);
    const type = blob.type;
    const preview = URL.createObjectURL(this.dataURLtoBlob(dataImage));
    const file = new File([blob], this.state.name, {
      type: type,
    });
    file.preview = preview;
    this.setState({ croppedFile: file, file: "" });
  };
  isValidValue = value => !!value;

  onDeleteContent = async () => {
    this.updateValue("");
    await this.waitFor(() => !this.props.value);
    this.setState({
      croppedFile: undefined,
      ...(this.props.showCroppingModal ? {} : { file: "" }),
    });
  };

  onContentUploaded = params => {
    if (params.length > 0) {
      this.updateValue(params[0].url);
    } else {
      this.setState({
        croppedFile: undefined,
        ...(this.props.showCroppingModal ? {} : { file: "" }),
      });
    }
    this.onUploaded();
  };

  onUploaded = () => {
    const { uploadStatus } = this.props;
    this.setState({
      uploadingImage: false,
      uploadObj: {},
    });
    this.onBlur();
    if (uploadStatus) uploadStatus(false);
  };

  onDropContent = params => {
    const { uploadStatus } = this.props;
    this.setState({ uploadObj: params });
    if (uploadStatus) uploadStatus(true);
  };

  updateFilesCount = count => {
    const { fileCount } = this.state;

    this.setState({
      uploadingImage: fileCount + count > 0,
      fileCount: fileCount + count,
    });
    this.onFocus();
  };

  onDeleteIconClick = e => {
    const { onDeleteClick } = this.props;
    e.stopPropagation();
    e.preventDefault();
    if (typeof onDeleteClick === "function") {
      onDeleteClick();
    }
  };

  renderComponent = () => {
    const {
      value,
      progressOfUploads,
      icon,
      parentId,
      parentType,
      enableFreeCropping = false,
      showAllImportOptions = false,
      dropdownOptions,
      testId,
      ratio,
      disabled,
      showActionsOnHover,
      variant,
      minHeight,
      editButtonType,
      dsVersion,
    } = this.props;

    const { uploadObj, uploadingImage, error } = this.state;
    const { id: uploadId, url: uploadUrl } = uploadObj || {};
    const progressValue = parseInt(
      _.get(
        progressOfUploads,
        `${uploadId}.progress`,
        uploadingImage ? "0" : "100"
      )
    );

    const url =
      this.state.croppedFile?.preview || this.state.file || uploadUrl || value;
    return (
      <Upload
        dsVersion={dsVersion}
        ratio={ratio}
        isUnsplashEnabled={false}
        restrictCroppingToImage={false}
        image={url}
        text={this.props.text}
        isCropModalOpen={
          this.state.file && this.props.showCroppingModal ? true : false
        }
        progress={progressValue}
        onDeleteClick={this.onDeleteContent}
        onCloseCropModal={this.onCancel}
        onUploadFromDevice={() =>
          this.dropzoneRef.current.refs.wrappedInstance.dialogueBoxConfirm()
        }
        onAddImageClick={image => {
          this.onAddContent(image);
        }}
        disabled={disabled}
        icon={icon}
        dropdownOptions={dropdownOptions}
        enableFreeCropping={enableFreeCropping}
        dropdownOverlay={
          showAllImportOptions ? (
            <CreateDropDown
              parentId={parentId}
              parentType={parentType}
              dropDownOptions={dropdownOptions}
              multiselect={false}
              isNewDropdown={true}
              isDropdownWithoutOverlay={true}
              attachmentExportOptions={["FILE"]}
              enableShareModal={false}
              accept="image/jpeg,image/png,image/jpg,image/heic,image/heif"
              addAttachment={this.onContentUploaded}
              customizedFile={this.croppedFile}
              onUploadFromDevice={() =>
                this.dropzoneRef.current.refs.wrappedInstance.dialogueBoxConfirm()
              }
              testId={`${testId}:create-dropdown`}
            />
          ) : undefined
        }
        testId={testId}
        showActionsOnHover={showActionsOnHover}
        minHeight={minHeight}
        editButtonType={editButtonType}
        variant={variant}
      />
    );
  };

  renderEdit = () => {
    const {
      value,
      parentId,
      parentType,
      showAllImportOptions = false,
      dropZoneStyle,
      disabled,
    } = this.props;
    const { uploadObj, error } = this.state;
    const { url: uploadUrl } = uploadObj || {};
    const disableClick =
      this.state.croppedFile?.preview ||
      this.state.file ||
      uploadUrl ||
      value ||
      showAllImportOptions;

    return (
      <DropzoneComponent
        multiple={false}
        ref={this.dropzoneRef}
        accept="image/jpeg,image/png,image/jpg,image/heic,image/heif"
        onContentUploaded={this.onContentUploaded}
        onDropContent={this.onDropContent}
        parentId={parentId}
        parentType={parentType}
        updateFilesCount={this.updateFilesCount}
        dropZoneStyle={dropZoneStyle}
        renderComponent={this.renderComponent()}
        customizedFileObject={this.customizedFileObject}
        disableClick={disableClick}
        customizedFile={this.croppedFile}
        mode={disabled ? "view" : "edit"}
      />
    );
  };
}

ProfilePhotoPickerV2.propTypes = {
  ...UIBaseComponent.propTypes,
  dsVersion: PropTypes.string,
};

ProfilePhotoPickerV2.defaultProps = {
  ...UIBaseComponent.defaultProps,
  ratio: "1:1",
  parentId: "ID1",
  parentType: "NONE",
  icon: <ProfileOutlined />,
  showCroppingModal: true,
  dropZoneStyle: { height: "160px", width: "160px" },
  disabled: false,
  showActionsOnHover: false,
  variant: "outlined",
  minHeight: "",
  editButtonType: "fill",
};

const mapStateToProps = (state, ownProps) => ({
  progressOfUploads: state.app_services.progressOfUploads,
});

export default compose(
  I18nHOC({ resource: ["common", "community"] }),
  connect(mapStateToProps, null, null, { withRef: true })
)(ProfilePhotoPickerV2);
